import React, { useState } from 'react';
import { LuSend } from 'react-icons/lu';

export const contactSchema = [
  {
    name: 'name',
    label: 'Full name',
    type: 'text',
    placeholder: 'Full name',
    defaultValue: '',
    required: true,
    validation: (value) => {
      if (!value.trim()) return 'Name is required.';
      if (value.length < 2) return 'Name must be at least 2 characters.';
      return null;
    },
  },
  {
    name: 'email',
    label: 'Email Address',
    type: 'email',
    placeholder: 'Email Address',
    defaultValue: '',
    required: true,
    validation: (value) => {
      if (!value.trim()) return 'Email address is required.';
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) return 'Please enter a valid email address.';
      return null;
    },
  },
  {
    name: 'message',
    label: 'Message',
    type: 'textarea',
    placeholder: 'Message',
    defaultValue: '',
    required: true,
    rows: 4,
    validation: (value) => {
      if (!value.trim()) return 'Message cannot be empty.';
      if (value.length < 10)
        return 'Message must be at least 10 characters long.';
      return null;
    },
  },
];

export default function ContactForm() {
  // Initialize form values dynamically from schema defaultValues
  const [formData, setFormData] = useState(() =>
    contactSchema.reduce((acc, field) => {
      acc[field.name] = field.defaultValue;
      return acc;
    }, {}),
  );

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Generic change handler for all inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for field on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // Validate all fields using schema rule functions
  const validateForm = () => {
    const newErrors = {};
    contactSchema.forEach((field) => {
      if (field.validation) {
        // Fallback to empty string if state entry is missing
        const fieldValue = formData[field.name] ?? '';
        const errorMsg = field.validation(fieldValue);
        if (errorMsg) {
          newErrors[field.name] = errorMsg;
        }
      }
    });
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 1. Validate form synchronously
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // 2. Clear previous errors & show loading state
    setErrors({});
    setIsSubmitting(true);

    try {
      // 3. Trigger Resend email delivery
      const response = await fetch('/api/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send email.');
      }

      setSubmitted(true);
    } catch (err) {
      setErrors({ submit: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {submitted ? (
        <div className="p-4 text-green-700 bg-green-100 border border-foreground rounded-2xl">
          Thank you! Your message has been sent successfully.
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          noValidate
          className="flex flex-col items-end justify-end px-5 py-5 space-y-5 text-sm border shadow-sm bg-foreground-light border-foreground rounded-2xl"
        >
          {contactSchema.map((field) => (
            <div key={field.name} className="flex flex-col w-full space-y-1">
              <label
                htmlFor={field.name}
                className="text-xs font-medium text-midground"
              >
                {field.label}{' '}
                {field.required && <span className="text-red-500">*</span>}
              </label>

              {/* Render Render Textarea */}
              {field.type === 'textarea' ? (
                <textarea
                  id={field.name}
                  name={field.name}
                  rows={field.rows || 3}
                  value={formData[field.name]}
                  placeholder={field.placeholder}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md outline-none resize-y placeholder:text-foreground border-foreground bg-input focus:ring-2 focus:ring-foreground-accent"
                />
              ) : (
                /* Render Standard Inputs (text, email, tel, etc.) */
                <input
                  id={field.name}
                  name={field.name}
                  type={field.type}
                  value={formData[field.name]}
                  placeholder={field.placeholder}
                  onChange={handleChange}
                  className="px-3 py-2 border rounded-md outline-none placeholder:text-foreground contact-input border-foreground bg-input focus:ring-2 focus:ring-foreground-accent"
                />
              )}

              {/* Display Validation Error */}
              {errors[field.name] && (
                <span className="mt-1 text-xs text-red-500">
                  {errors[field.name]}
                </span>
              )}
            </div>
          ))}

          <button
            type="submit"
            disabled={isSubmitting}
            className="flex flex-row items-center px-5 py-3 text-sm gap-x-2 w-fit lg:text-base gradient-button-2 group"
          >
            <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
            <div className="relative w-5 h-5 overflow-hidden">
              <div className="absolute transition-all duration-200 group-hover:-translate-y-5 group-hover:translate-x-4">
                <LuSend className="w-5 h-5" />
                <LuSend className="w-5 h-5 -translate-x-4" />
              </div>
            </div>
          </button>
        </form>
      )}
    </div>
  );
}
